import { useState } from 'react';
import type { SaUser } from '@/mocks/saUsers';
import { userTypeLabels, userTypeAccents, statusLabels, statusAccents, verificationLabels, verificationAccents } from './userLabels';

interface UserTableProps {
  users: SaUser[];
  onSelect: (user: SaUser) => void;
  onAction: (action: string, user: SaUser) => void;
}

export default function UserTable({ users, onSelect, onAction }: UserTableProps) {
  const [openActionMenu, setOpenActionMenu] = useState<string | null>(null);

  const actionsConfig: { id: string; label: string; icon: string; condition?: (u: SaUser) => boolean }[] = [
    { id: 'view', label: 'Ver perfil completo', icon: 'ri-eye-line' },
    { id: 'suspend', label: 'Suspender cuenta', icon: 'ri-pause-circle-line', condition: (u) => u.accountStatus === 'activo' },
    { id: 'reactivate', label: 'Reactivar cuenta', icon: 'ri-play-circle-line', condition: (u) => u.accountStatus === 'suspendido' },
    { id: 'block', label: 'Bloquear cuenta', icon: 'ri-forbid-line', condition: (u) => u.accountStatus !== 'bloqueado' },
    { id: 'verify', label: 'Verificar cuenta', icon: 'ri-verified-badge-line', condition: (u) => u.verificationStatus !== 'verificado' },
    { id: 'reset', label: 'Restablecer acceso', icon: 'ri-key-2-line' },
    { id: 'close_sessions', label: 'Cerrar sesiones activas', icon: 'ri-logout-box-r-line', condition: (u) => u.activeSessions > 0 },
    { id: 'view_activity', label: 'Ver actividad', icon: 'ri-history-line' },
    { id: 'view_reports', label: 'Ver reportes', icon: 'ri-flag-line', condition: (u) => u.reportsCount > 0 },
    { id: 'view_moderation', label: 'Historial de moderación', icon: 'ri-shield-check-line', condition: (u) => u.moderationFlags > 0 },
    { id: 'view_payments', label: 'Pagos relacionados', icon: 'ri-money-dollar-circle-line', condition: (u) => u.totalPayments > 0 },
  ];

  const getAvailableActions = (user: SaUser) => {
    return actionsConfig.filter((a) => !a.condition || a.condition(user));
  };

  return (
    <div className="bg-background-50 border border-background-200 rounded-2xl overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[1600px]">
          <thead>
            <tr className="border-b border-background-200 bg-background-100/50">
              <th className="text-left px-3 py-3 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">Usuario</th>
              <th className="text-left px-3 py-3 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">Email</th>
              <th className="text-left px-3 py-3 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">Teléfono</th>
              <th className="text-left px-3 py-3 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">Tipo</th>
              <th className="text-left px-3 py-3 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">Rol</th>
              <th className="text-left px-3 py-3 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">Radio</th>
              <th className="text-left px-3 py-3 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">País</th>
              <th className="text-left px-3 py-3 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">Estado</th>
              <th className="text-left px-3 py-3 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">Verificación</th>
              <th className="text-left px-3 py-3 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">Registro</th>
              <th className="text-left px-3 py-3 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">Último Acceso</th>
              <th className="text-center px-3 py-3 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {users.length === 0 ? (
              <tr>
                <td colSpan={12} className="px-3 py-12 text-center">
                  <div className="flex flex-col items-center gap-2">
                    <div className="w-12 h-12 rounded-2xl bg-background-100 flex items-center justify-center">
                      <i className="ri-user-search-line text-foreground-400 text-xl"></i>
                    </div>
                    <p className="text-foreground-500 text-sm font-medium">No se encontraron usuarios</p>
                    <p className="text-foreground-400 text-xs">Probá ajustando los filtros de búsqueda</p>
                  </div>
                </td>
              </tr>
            ) : (
              users.map((user) => (
                <tr key={user.id} className="border-b border-background-200/60 hover:bg-background-100/40 transition-colors">
                  <td className="px-3 py-2.5">
                    <button
                      onClick={() => onSelect(user)}
                      className="flex items-center gap-2 cursor-pointer hover:opacity-80 transition-opacity"
                    >
                      <div className="w-7 h-7 rounded-lg bg-foreground-950 flex items-center justify-center shrink-0">
                        <span className="text-background-50 text-[10px] font-bold">{user.avatar}</span>
                      </div>
                      <span className="text-foreground-800 text-xs font-semibold whitespace-nowrap">{user.name}</span>
                    </button>
                  </td>
                  <td className="px-3 py-2.5 text-foreground-600 text-xs">{user.email}</td>
                  <td className="px-3 py-2.5 text-foreground-600 text-xs whitespace-nowrap">{user.phone}</td>
                  <td className="px-3 py-2.5">
                    <span className={`inline-flex px-2 py-0.5 rounded-md text-[10px] font-semibold border ${userTypeAccents[user.userType]}`}>
                      {userTypeLabels[user.userType]}
                    </span>
                  </td>
                  <td className="px-3 py-2.5 text-foreground-700 text-xs whitespace-nowrap">{user.mainRole}</td>
                  <td className="px-3 py-2.5 text-foreground-600 text-xs whitespace-nowrap">{user.associatedRadio}</td>
                  <td className="px-3 py-2.5 text-foreground-600 text-xs">{user.country}</td>
                  <td className="px-3 py-2.5">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${statusAccents[user.accountStatus]}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${user.accountStatus === 'activo' ? 'bg-green-500' : user.accountStatus === 'suspendido' ? 'bg-amber-500' : user.accountStatus === 'bloqueado' ? 'bg-red-500' : 'bg-blue-500'}`}></span>
                      {statusLabels[user.accountStatus]}
                    </span>
                  </td>
                  <td className="px-3 py-2.5">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${verificationAccents[user.verificationStatus]}`}>
                      {user.verificationStatus === 'verificado' && <i className="ri-verified-badge-fill text-[10px]"></i>}
                      {verificationLabels[user.verificationStatus]}
                    </span>
                  </td>
                  <td className="px-3 py-2.5 text-foreground-500 text-[11px] whitespace-nowrap">{user.registrationDate}</td>
                  <td className="px-3 py-2.5 text-foreground-500 text-[11px] whitespace-nowrap">{user.lastAccess}</td>
                  <td className="px-3 py-2.5 text-center relative">
                    <button
                      onClick={() => setOpenActionMenu(openActionMenu === user.id ? null : user.id)}
                      className="w-7 h-7 rounded-lg bg-background-100 flex items-center justify-center cursor-pointer hover:bg-background-200 transition-colors"
                    >
                      <i className="ri-more-2-fill text-foreground-500 text-sm"></i>
                    </button>
                    {openActionMenu === user.id && (
                      <>
                        <div className="fixed inset-0 z-40" onClick={() => setOpenActionMenu(null)}></div>
                        <div className="absolute right-0 top-full mt-1 z-50 w-56 bg-background-50 border border-background-200 rounded-xl shadow-lg py-1">
                          {getAvailableActions(user).map((action) => (
                            <button
                              key={action.id}
                              onClick={() => {
                                onAction(action.id, user);
                                setOpenActionMenu(null);
                              }}
                              className="w-full flex items-center gap-2.5 px-3 py-2 text-xs text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap text-left"
                            >
                              <i className={`${action.icon} text-foreground-400 text-sm`}></i>
                              {action.label}
                            </button>
                          ))}
                        </div>
                      </>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between px-4 py-3 border-t border-background-200 bg-background-100/30">
        <span className="text-foreground-400 text-[10px] font-medium">
          Mostrando {users.length} usuario{users.length !== 1 ? 's' : ''}
        </span>
        <div className="flex items-center gap-1">
          <button className="w-7 h-7 rounded-lg bg-background-100 flex items-center justify-center cursor-not-allowed opacity-40">
            <i className="ri-arrow-left-s-line text-foreground-500 text-sm"></i>
          </button>
          <button className="w-7 h-7 rounded-lg bg-foreground-950 flex items-center justify-center cursor-default">
            <span className="text-background-50 text-[10px] font-bold">1</span>
          </button>
          <button className="w-7 h-7 rounded-lg bg-background-100 flex items-center justify-center cursor-pointer hover:bg-background-200 transition-colors">
            <i className="ri-arrow-right-s-line text-foreground-500 text-sm"></i>
          </button>
        </div>
      </div>
    </div>
  );
}